import { useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import {
  PayrollRunDialog,
  PayrollRunsFilters,
  type PayrollRunsFiltersValue,
} from "@/features/payroll/components";
import { usePayrollTranslation } from "@/features/payroll/i18n/use-payroll-translation";
import type { TransactionFilter } from "@/features/wallet/api/dto";
import { useTransactions } from "@/features/wallet/api/use-transactions";
import { transactionsColumns } from "@/features/wallet/components";

/**
 * Suivi des lots de paie (Phase 9) — la paie est un type de transaction du
 * wallet-service (`PAYROLL_CREDIT`, §6.3) : la liste réutilise le hook et
 * les colonnes de `features/wallet` avec le filtre `type` verrouillé. Le
 * détail d'un versement (reçu + polling de statut, §5.3) s'ouvre sur la
 * page détail des transactions via l'action « Voir » des lignes.
 */
export function PayrollRunsPage() {
  const { t } = usePayrollTranslation();
  const [runOpen, setRunOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<PayrollRunsFiltersValue>({});

  const params = useMemo(() => {
    const filter: TransactionFilter = { type: "PAYROLL_CREDIT" };
    if (filters.status) filter.status = filters.status;
    return {
      filter,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    };
  }, [filters, pagination]);

  const { data, isLoading, isFetching, error, refetch } = useTransactions(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t("payroll.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("payroll.subtitle")}</p>
        </div>
        <Button onClick={() => setRunOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("payroll.create")}
        </Button>
      </div>

      <PayrollRunsFilters
        value={filters}
        onChange={(value) => {
          setFilters(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />

      <DataTableClar
        data={data?.items ?? []}
        columnsClar={transactionsColumns}
        title={t("payroll.title")}
        exportFileName="payroll-runs"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <PayrollRunDialog open={runOpen} onOpenChange={setRunOpen} />
    </section>
  );
}
